// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { Button, Modal } from '@imprivata-cloud/components';
import { Flex, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserDetails } from '../../../api/portalServices';
import { type ImprUser, isAdmin } from '../../../api/types';
import MenuIcon from '../../../assets/icons/kabob_menu.svg?react';
import QuestionImg from '../../../assets/icons/question.svg?react';
import { isCurrentUser, useSession } from '../../../common/authorization/context/Session';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '../../../errorHandler/errors';
import { useDeleteIdentityUser, useIdentityDir } from '../../hooks/useIdentityDir';
import ImprListView from '../users/impr-list-view/ImprListView';
import IdentityUsersEmpty from './empty/IdentityUsersEmpty';
import useIdpInfiniteScroll from './hooks/useIdpInfiniteScroll';
import { useUnlockUser } from './hooks/useUnlockUser';
import UserModal from './UserModal';

const TRANSLATION_ROOT = 'portal.settings.identity-users';
const TRANSLATION_REMOVE = 'common.remove';

const IdentityUsers = () => {
  const { t } = useTranslation();
  const { data: directories, isFetching, error, refetch: refetchDirectories } = useIdentityDir();
  const [searchVal, setSearchVal] = useState<string>('');
  const [usersList, setUsersList] = useState<ImprUser[]>([]);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const { emitError } = useErrorContext();
  const lastElementRef = useRef<HTMLDivElement | null>(null);
  const [editUserData, setEditUserData] = useState<ImprUser>();
  const { mutate: deleteUser } = useDeleteIdentityUser();
  const { mutate: unlockUserMutation, isPending: isUnlockUserPending } = useUnlockUser();
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const { userInfo: sessionUserInfo } = useSession();
  const [unlockedUserId, setUnlockedUserId] = useState<string | null>(null);

  const {
    data: users = [],
    isFetchingNextPage,
    refetch: refetchUsers,
    isLoading,
    error: errorUsers,
  } = useIdpInfiniteScroll({ lastElementRef });

  useEffect(() => {
    if (error || errorUsers) {
      console.error('Error fetching identity users:', error);
      emitError(error as AppError);
    }
    if (users) {
      setUsersList(users);
    }
  }, [error, errorUsers, users, emitError]);

  const refetchUserList = useCallback(async () => {
    console.log('[IdentityUsers] refetchUserList');
    refetchUsers();
  }, [refetchUsers]);

  const unlockUser = useCallback(
    (userId: string) => {
      unlockUserMutation(
        { unlockRequest: { userId } },
        {
          onSuccess: () => {
            console.log('User unlocked successfully:', userId);
            setUnlockedUserId(userId);
          },
          onError: (e: unknown) => {
            console.error('Error unlocking user:', e);
            emitError(new AppError(e as ApiError));
          },
        },
      );
    },
    [unlockUserMutation, emitError],
  );

  const showUnlockUserButton = useCallback((user: ImprUser): boolean => {
    if (user) {
      return user.lockedUntil ? new Date(user.lockedUntil).getTime() > Date.now() : false;
    }
    return false;
  }, []);

  const editUser = async (userId: string) => {
    console.debug('edit user', userId);
    try {
      const userDetails = await getUserDetails({ userId: userId });
      const editUserInfo = userDetails.user;
      setEditUserData(editUserInfo);
      setIsUserModalOpen(true);
    } catch (e) {
      console.error('Error fetching user details:', e);
      emitError(e as AppError);
    }
  };

  const handleSearch = () => {
    console.log('searching data');
    if (!searchVal) {
      // If search value is empty, reset the users list to the original data
      setUsersList(users || []);
      return;
    }
    const filteredUsers = usersList.filter(user =>
      user.displayName?.toLowerCase().includes(searchVal.toLowerCase()),
    );
    setUsersList(filteredUsers);
  };

  const handleSearchChange = (searchValue: string) => {
    setSearchVal(searchValue);
  };

  if (isFetching || isLoading) {
    return <></>;
  }

  const adminDeleteDisabled = usersList.filter(isAdmin).length < 2;

  return (
    <div>
      <ImprListView
        adminDeleteDisabled={adminDeleteDisabled}
        enableMenu={true}
        menuIcon={<MenuIcon />}
        dataSource={usersList || []}
        customEmptyListNode={
          <IdentityUsersEmpty
            directories={directories}
            refetchDirectories={refetchDirectories}
            refetchUsers={refetchUserList}
          />
        }
        sortLabel={'All users (by name)'}
        handleSearch={handleSearch}
        onSearchChange={handleSearchChange}
        headerExtraComponents={[
          <div key={'add-user'}>
            {!!usersList?.length && (
              <Button
                aria-label="button-add-user"
                type={'link'}
                onClick={() => setIsUserModalOpen(true)}
                label={t(`${TRANSLATION_ROOT}.add-user`)}
              />
            )}
          </div>,
        ]}
        enableEdit
        editUser={editUser}
        deleteUser={(userId: string) => {
          setDeletingUserId(userId);
        }}
        enableUnlock={showUnlockUserButton}
        unlockUser={(userId: string) => unlockUser(userId)}
        unlockPending={isUnlockUserPending}
        isUserUnlocked={(userId: string) => unlockedUserId === userId}
      />
      <div ref={lastElementRef} data-testid="user-list-last-element">
        {isFetchingNextPage && (
          <Flex justify={'center'} style={{ padding: 15 }}>
            <Spin data-testid={'loading-indicator'} size="small" />
          </Flex>
        )}
      </div>
      <div key={'userMenu'}>
        {isUserModalOpen && (
          <UserModal
            editUserData={editUserData}
            domain={directories?.[0].domains?.[0].domainName}
            closeModal={() => {
              setEditUserData(undefined);
              setIsUserModalOpen(false);
            }}
            refetchUsers={refetchUserList}
            adminDeleteDisabled={adminDeleteDisabled}
            isCurrentUser={isCurrentUser(editUserData?.username, sessionUserInfo)}
          />
        )}
        <Modal
          aria-label="user-modal"
          centered={true}
          open={!!deletingUserId}
          onOk={() =>
            deleteUser(
              { userId: deletingUserId || '' },
              {
                onSuccess: () => {
                  setDeletingUserId(null);
                  refetchUsers();
                },
              },
            )
          }
          onCancel={() => setDeletingUserId(null)}
          okText={t(TRANSLATION_REMOVE)}
          cancelText={t('common.do-not-remove')}
          title={t(TRANSLATION_REMOVE)}
        >
          <div style={{ padding: '1rem' }}>
            <QuestionImg style={{ margin: '1rem' }} />
            <p>{t(`${TRANSLATION_ROOT}.remove-user`)}</p>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default IdentityUsers;
